// =============================================
// AuditPanel — Map Audit v2 (G3) — 5 warstw + SRO LLM
// Full refactor po M16. Stary scnAuditor legacy tylko w audit_report.legacy_scn_audit.
// =============================================

import { useState, useMemo } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  Info,
  Check,
  Clock,
  ChevronDown,
  ChevronRight,
  Zap,
  Sparkles,
  Activity,
  Target,
  Download,
  RotateCcw,
  Wrench,
  Link2,
} from "lucide-react";
import { healAllPendingForMap } from "@/services/healPendingLinks";
import { useAuditStatus } from "@/hooks/useAuditStatus";
import { AuditActionBadge } from "./AuditActionBadge";
import { MapRepairModal } from "./MapRepairModal";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Eye } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { estimateLlmAuditCost, estimateLlmAuditDuration } from "@/services/validation/auditCostEstimator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useAuditMap } from "@/hooks/useAuditMap";
import type { AuditReportV2, AuditVerdict, AuditIssue } from "@/types/thematicMaps";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

interface AuditPanelProps {
  mapId: string;
}

function verdictColor(v: AuditVerdict | undefined): string {
  if (v === "pass") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
  if (v === "warn") return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";
  if (v === "fail") return "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30";
  return "bg-zinc-500/15 text-zinc-500 border-zinc-500/30";
}

function severityIcon(s: string) {
  if (s === "error")
    return <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />;
  if (s === "warning")
    return <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />;
  return <Info className="h-3.5 w-3.5 text-sky-500 shrink-0" />;
}

function LayerTile({
  label,
  score,
  max,
  verdict,
  icon,
  detail,
}: {
  label: string;
  score: number | undefined;
  max: number | undefined;
  verdict: AuditVerdict | undefined;
  icon: React.ReactNode;
  detail?: string;
}) {
  const hasData = score !== undefined && max !== undefined;
  return (
    <div className={`rounded-lg border p-3 ${verdictColor(verdict)}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-70">
        {icon}
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-xl font-bold tabular-nums">
          {hasData ? score : "—"}
        </span>
        {hasData && <span className="text-xs opacity-60">/{max}</span>}
      </div>
      {detail && <p className="text-[10px] opacity-70 mt-0.5">{detail}</p>}
    </div>
  );
}

/**
 * Sekcja z opisem + scrollowalnym body.
 * Zamiast rozlewać wszystko w nieskończoność — trzyma dane w fixed height
 * z własnym scrollem, żeby widok mieścił się na ekranie.
 */
function SectionBox({
  title,
  description,
  badge,
  children,
  maxHeight = "300px",
  dense = false,
}: {
  title: string;
  description: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  maxHeight?: string;
  dense?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-sm">{title}</h4>
          {badge}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
      </div>
      <div className={`${dense ? "p-2" : "p-3"} overflow-y-auto`} style={{ maxHeight }}>
        {children}
      </div>
    </div>
  );
}

function ScoreBar({ score, maxScore }: { score: number; maxScore: number }) {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-40 h-2.5 bg-muted rounded-full overflow-hidden shrink-0">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold tabular-nums shrink-0">
        {score}/{maxScore}
      </span>
    </div>
  );
}

export function AuditPanel({ mapId }: AuditPanelProps) {
  const { auditReport, fullAudit, fetchPaa } = useAuditMap(mapId);
  // Status badges per 4 buttony — read-only z DB (audit_report.meta, last_repaired_at, dataforseo_cache)
  const auditStatusQuery = useAuditStatus(mapId);
  const status = auditStatusQuery.data;
  const { toast } = useToast();
  const qc = useQueryClient();
  const [logs, setLogs] = useState<string[]>([]);
  const [showResetDialog, setShowResetDialog] = useState<null | "deterministic" | "llm" | "paa">(null);
  const [resetting, setResetting] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showPaaDialog, setShowPaaDialog] = useState(false);
  // STOP-4 link-resolver — re-heal pending links UI
  const [healing, setHealing] = useState(false);
  const [showHealDialog, setShowHealDialog] = useState(false);

  const handleReHealAllPending = async () => {
    setHealing(true);
    try {
      const r = await healAllPendingForMap(mapId);
      const errCount = r.errors.length;
      toast({
        title:
          r.healed > 0
            ? `Zhealowano ${r.linksHealed} linków w ${r.healed} wpisach`
            : r.scanned > 0
              ? `Brak nowych targetów (pominięte: ${r.scanned}, still pending: ${r.stillPending})`
              : "Brak markerów w tej mapie — nic do heal",
        description:
          errCount > 0
            ? `Błędy: ${errCount} (sprawdź konsolę)`
            : r.scanned === 0
              ? undefined
              : `Scanned: ${r.scanned} · still pending: ${r.stillPending}`,
        variant: errCount > 0 ? "destructive" : "default",
      });
      if (errCount > 0) {
        console.warn("[ReHeal] errors:", r.errors);
      }
      setShowHealDialog(false);
    } catch (e) {
      toast({
        title: "Heal failed",
        description: e instanceof Error ? e.message : "unknown",
        variant: "destructive",
      });
    } finally {
      setHealing(false);
    }
  };

  // PAA viewer — laduje nody z paa_preview dla tej mapy
  const paaNodesQuery = useQuery({
    queryKey: ["thematic-nodes-paa", mapId],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("thematic_nodes")
        .select("id, slug, title, canonical_question, paa_preview, paa_fetched_at")
        .eq("map_id", mapId)
        .not("paa_fetched_at", "is", null)
        .order("slug");
      if (error) throw error;
      return (data || []) as Array<{
        id: string;
        slug: string;
        title: string | null;
        canonical_question: string | null;
        paa_preview: Array<{ question: string; source: string }> | null;
        paa_fetched_at: string | null;
      }>;
    },
    enabled: !!mapId,
  });

  const report = auditReport.data?.audit_report as AuditReportV2 | undefined;
  const meta = report?.meta;
  const layers = report?.layers ?? {};

  // Seed count dla dynamic cost labelu button "Pełny audyt + LLM Sonnet"
  const seedCountQuery = useQuery({
    queryKey: ["thematic-seeds-count", mapId],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { count, error } = await (supabase as any)
        .from("thematic_seeds")
        .select("id", { count: "exact", head: true })
        .eq("map_id", mapId);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!mapId,
  });
  const seedCount = seedCountQuery.data ?? 0;
  const dynamicAuditCost = estimateLlmAuditCost(seedCount);
  const dynamicAuditDuration = estimateLlmAuditDuration(seedCount);

  const addLog = (msg: string) => setLogs((prev) => [...prev.slice(-100), msg]);

  // Aggregate all issues from all layers
  const allIssues: Array<AuditIssue & { layer: string }> = useMemo(() => {
    const list: Array<AuditIssue & { layer: string }> = [];
    const addFromLayer = (layer: string, layerData: unknown) => {
      const issues = (layerData as { issues?: AuditIssue[] })?.issues;
      if (Array.isArray(issues)) {
        for (const i of issues) list.push({ ...i, layer });
      }
    };
    addFromLayer("L2-struct", layers.L2_deterministic);
    addFromLayer("L3", layers.L3);
    addFromLayer("L5", layers.L5_coverage);
    // L2 LLM: critical_findings
    const llmFindings = (layers.L2_llm as { critical_findings?: AuditIssue[] })?.critical_findings;
    if (Array.isArray(llmFindings)) {
      for (const i of llmFindings) list.push({ ...i, layer: "L2-LLM" });
    }
    // Sort by severity
    const order = { error: 0, warning: 1, info: 2, ok: 3 } as const;
    list.sort((a, b) => order[a.severity as keyof typeof order] - order[b.severity as keyof typeof order]);
    return list;
  }, [layers]);

  const errorCount = allIssues.filter((i) => i.severity === "error").length;
  const warningCount = allIssues.filter((i) => i.severity === "warning").length;
  const infoCount = allIssues.filter((i) => i.severity === "info").length;

  const handleExportAudit = () => {
    if (!report) {
      toast({ title: "Brak audytu do eksportu", description: "Najpierw kliknij 'Przelicz audyt'", variant: "destructive" });
      return;
    }
    const payload = {
      export_meta: {
        exported_at: new Date().toISOString(),
        map_id: mapId,
        audit_score: auditReport.data?.audit_score,
        audited_at: auditReport.data?.audited_at,
      },
      audit_report: report,
    };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    const filename = `audit-${mapId.slice(0, 8)}-${stamp}.json`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Eksport gotowy", description: `${filename} (${(blob.size / 1024).toFixed(0)} KB)` });
    addLog(`📦 Eksport: ${filename} (${(blob.size / 1024).toFixed(0)} KB)`);
  };

  const handleResetPaa = async () => {
    setResetting(true);
    addLog("🗑 Reset PAA — czyszczenie paa_preview wszystkich nodów...");
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error, count } = await (supabase as any)
        .from("thematic_nodes")
        .update({ paa_preview: [], paa_fetched_at: null }, { count: "exact" })
        .eq("map_id", mapId);
      if (error) throw error;
      addLog(`✓ PAA wyzerowane dla ${count ?? "?"} nodów — kliknij "1 Pobierz PAA" od nowa`);
      toast({
        title: "PAA wyzerowane",
        description: `paa_preview=[] + paa_fetched_at=NULL dla ${count ?? "?"} nodów. Kliknij "1 Pobierz PAA" by pobrać od zera.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`✗ Reset PAA failed: ${msg}`);
      toast({ title: "Błąd resetu PAA", description: msg, variant: "destructive" });
    } finally {
      setResetting(false);
      setShowResetDialog(null);
    }
  };

  const handleResetAudit = async (mode: "deterministic" | "llm") => {
    setResetting(true);
    const label = mode === "deterministic" ? "deterministic (L1+L2det+L3+L5)" : "LLM Sonnet (L2_llm)";
    addLog(`🗑 Reset ${label} — usuwam z audit_report...`);
    try {
      // Pobierz bieżący raport
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: row } = await (supabase as any)
        .from("thematic_maps")
        .select("audit_report")
        .eq("id", mapId)
        .maybeSingle();
      const current = (row?.audit_report ?? {}) as { layers?: Record<string, unknown>; meta?: Record<string, unknown>; legacy_scn_audit?: unknown };
      const layers = { ...(current.layers ?? {}) };

      if (mode === "deterministic") {
        delete layers.L1;
        delete layers.L2_deterministic;
        delete layers.L3;
        delete layers.L5_coverage;
      } else {
        delete layers.L2_llm;
      }

      const anyLayerLeft = Object.keys(layers).length > 0;
      // Jeśli po resecie nie ma żadnych warstw → hard clean (wyrzuć stare meta też)
      // Inaczej UI widzi "report istnieje" ale layers = {} i pokazuje puste kafelki.
      const newReport = anyLayerLeft
        ? { ...current, layers }
        : {}; // clean slate
      const updates: Record<string, unknown> = {
        audit_report: newReport,
        ...(anyLayerLeft ? {} : { audit_score: null, audited_at: null }),
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("thematic_maps")
        .update(updates)
        .eq("id", mapId);
      if (error) throw error;

      qc.invalidateQueries({ queryKey: ["audit-report", mapId] });
      addLog(`✓ ${label} wyzerowane — kliknij odpowiedni "Przelicz" od nowa`);
      toast({
        title: `Reset ${label}`,
        description: mode === "deterministic"
          ? "L1+L2det+L3+L5 usunięte. L2 LLM zachowany. PAA nietknięty."
          : "L2 LLM Sonnet usunięty. Deterministic zachowany. PAA nietknięty.",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`✗ Reset failed: ${msg}`);
      toast({ title: "Błąd resetu", description: msg, variant: "destructive" });
    } finally {
      setResetting(false);
      setShowResetDialog(null);
    }
  };

  const handleFetchPaa = async (forceRefresh = false) => {
    addLog(`▶ Pobieranie PAA baseline (DataForSEO${forceRefresh ? ", FORCE REFRESH" : ", cache 30d"})...`);
    try {
      const stats = await fetchPaa.mutateAsync({
        onLog: (_type, msg) => addLog(msg),
        forceRefresh,
      });
      addLog(
        `✓ PAA done: fetched=${stats.fetched} questions=${stats.total_questions}`,
      );
      toast({
        title: "PAA baseline gotowe",
        description: `Fetched ${stats.fetched}, pytań ${stats.total_questions}. Kliknij "Pokaż PAA" żeby zobaczyć.`,
      });
      // Refresh + auto-otworz dialog z pytaniami
      await qc.invalidateQueries({ queryKey: ["thematic-nodes-paa", mapId] });
      if (stats.fetched > 0) setShowPaaDialog(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`✗ PAA failed: ${msg}`);
      toast({ title: "Błąd PAA", description: msg, variant: "destructive" });
    }
  };

  const handleAudit = async (skipLLM: boolean) => {
    addLog(`▶ Map Audit v2 start (${skipLLM ? "deterministic only" : "+ LLM Sonnet"})`);
    try {
      const res = await fullAudit.mutateAsync({
        skipLLM,
        onLog: (_type, msg) => addLog(msg),
      });
      if (res.skipped_by_lock) {
        addLog("ℹ Audyt pominięty — inny audyt już trwa");
        toast({ title: "Audyt pominięty", description: "Inny audyt już trwa dla tej mapy" });
      } else if (res.report) {
        addLog(
          `✓ Audit done: score=${res.report.meta.total_score}/${res.report.meta.max_score} verdict=${res.report.meta.overall_verdict}`,
        );
        toast({
          title: "Audyt zakończony",
          description: `Score ${res.report.meta.total_score}/100 (${res.report.meta.overall_verdict})`,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`✗ Audit failed: ${msg}`);
      toast({ title: "Błąd audytu", description: msg, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-violet-600" />
                Audyt mapy — 5 warstw SRO (v2)
              </CardTitle>
              <CardDescription>
                L1 post-validate · L2 deterministic · L2 LLM (Sonnet 4.6) · L3 semantic · L5 coverage wpisów WP.
                Auto-run po generacji nodów + matcherze. Manual recompute poniżej.
              </CardDescription>
            </div>
            <div className="text-right shrink-0">
              {meta?.total_score !== undefined && (
                <>
                  <div className="text-3xl font-bold tabular-nums">
                    <span className={
                      meta.overall_verdict === "pass" ? "text-emerald-600 dark:text-emerald-400"
                        : meta.overall_verdict === "warn" ? "text-amber-600 dark:text-amber-400"
                        : "text-red-600 dark:text-red-400"
                    }>
                      {meta.total_score}
                    </span>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </div>
                  <Badge className={`${verdictColor(meta.overall_verdict)} border text-[10px] uppercase`}>
                    {meta.overall_verdict}
                  </Badge>
                  {meta.audited_at && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      <Clock className="h-3 w-3 inline mr-0.5" />
                      {formatDistanceToNow(new Date(meta.audited_at), { addSuffix: true, locale: pl })}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* 2-kolumnowy layout: akcje po lewej, debug box po prawej */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4">
            {/* LEWA: normalne akcje użytkownika */}
            <div className="space-y-3">
              {/* Instrukcja */}
              <div className="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
                <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 mb-1.5">
                  📋 Kolejność kliknięć: 1 → 2 → 3
                </p>
                <ol className="text-[11px] text-muted-foreground space-y-1 ml-4 list-decimal">
                  <li><strong>Pobierz PAA</strong> — raz (cache 30 dni). Delta pomija świeże nody.</li>
                  <li><strong>Przelicz audyt (deterministic)</strong> — darmowe, ~2s. L1+L2 structural+L3+L5.</li>
                  <li>
                    <strong>Pełny audyt + LLM Sonnet</strong> — dokłada L2 LLM SRO{" "}
                    {seedCount > 0 ? (
                      <>
                        (~${dynamicAuditCost.toFixed(2)}, ~{dynamicAuditDuration}s dla {seedCount} seedów).
                      </>
                    ) : (
                      "(~$0.077/seed, ~2s/seed)."
                    )}
                  </li>
                </ol>
              </div>

              {/* 4 przyciski akcji w kolejności: 1 PAA → 2 Det → 3 LLM → 4 Napraw */}
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <Button
                    size="sm"
                    onClick={() => handleFetchPaa(false)}
                    disabled={fetchPaa.isPending}
                    className="flex-1 justify-start"
                    variant="outline"
                  >
                    <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center mr-2 shrink-0">1</span>
                    {fetchPaa.isPending ? "Pobieranie PAA..." : "Pobierz PAA (delta, cache 30d)"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowPaaDialog(true)}
                    disabled={(paaNodesQuery.data?.length ?? 0) === 0}
                    title={
                      (paaNodesQuery.data?.length ?? 0) === 0
                        ? "Najpierw pobierz PAA (przycisk 1)"
                        : `Pokaz ${paaNodesQuery.data?.length} nodow z pobranym PAA`
                    }
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Pokaz PAA ({paaNodesQuery.data?.length ?? 0})
                  </Button>
                  {status && (
                    <AuditActionBadge
                      lastAt={status.paa.lastAt}
                      detail={status.paa.detail}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAudit(true)}
                    disabled={fullAudit.isPending}
                    className="flex-1 justify-start"
                  >
                    <span className="w-5 h-5 rounded-full bg-white text-violet-600 text-[10px] font-bold flex items-center justify-center mr-2 shrink-0">2</span>
                    {fullAudit.isPending ? "Audytuję..." : "Przelicz audyt (deterministic) — darmowe, 2s"}
                  </Button>
                  {status && (
                    <AuditActionBadge
                      lastAt={status.deterministic.lastAt}
                      detail={status.deterministic.detail}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAudit(false)}
                    disabled={fullAudit.isPending}
                    className="flex-1 justify-start bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <span className="w-5 h-5 rounded-full bg-white text-violet-600 text-[10px] font-bold flex items-center justify-center mr-2 shrink-0">3</span>
                    <Sparkles className="h-3 w-3 mr-1" />
                    {fullAudit.isPending
                      ? "Audytuję..."
                      : seedCount > 0
                      ? `Pełny audyt + LLM (Sonnet 4.6) — ~$${dynamicAuditCost.toFixed(2)}/mapa · ~${dynamicAuditDuration}s · ${seedCount} seedów`
                      : "Pełny audyt + LLM (Sonnet 4.6)"}
                  </Button>
                  {/* LLM badge celowo NIE pokazywany — audit_report.meta.llm_used wraca na false
                      po każdym kroku 2 lub Napraw, więc "nie odpalany" jest false-negative.
                      Reliable tracking wymaga osobnego pola last_llm_audit_at (TODO future). */}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowRepairModal(true)}
                    disabled={!layers.L2_deterministic}
                    className="flex-1 justify-start bg-emerald-600 hover:bg-emerald-700 text-white"
                    title={
                      layers.L2_deterministic
                        ? "Auto-merge duplikatów, usunięcie broken relations, kolejka propozycji"
                        : "Wymaga świeżego audytu deterministycznego (krok 2)"
                    }
                  >
                    <span className="w-5 h-5 rounded-full bg-white text-emerald-700 text-[10px] font-bold flex items-center justify-center mr-2 shrink-0">4</span>
                    <Wrench className="h-3 w-3 mr-1" />
                    Napraw mapę (auto + review queue)
                  </Button>
                  {status && (
                    <AuditActionBadge
                      lastAt={status.repair.lastAt}
                      detail={status.repair.detail}
                    />
                  )}
                </div>
              </div>

              {/* Eksport + Re-heal pending */}
              <div className="flex items-center gap-2 pt-2 border-t border-border/40 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportAudit}
                  disabled={!layers.L2_deterministic && !layers.L2_llm}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Eksportuj dane audytu (JSON)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHealDialog(true)}
                  disabled={healing}
                  title="Skanuje wpisy w tej mapie z markerem data-pending-link i podmienia na <a href> dla targetów które są już live (UPDATE w DB + WP REST)"
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Re-heal pending links
                </Button>
              </div>
            </div>

            {/* PRAWA: 🛠 DEBUG BOX — tylko reset buttony */}
            <div className="rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">🛠</span>
                <h3 className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                  Okno debugowania
                </h3>
                <Badge variant="outline" className="text-[8px] border-amber-500/40 text-amber-700 dark:text-amber-400">
                  dev-only
                </Badge>
              </div>
              <p className="text-[10px] text-muted-foreground mb-3 leading-snug">
                Tylko do pracy z kodem podczas dokańczania systemu. Zeruj output konkretnego kroku
                żeby przetestować poprawiony kod bez re-runu poprzedników. W finalnej wersji klienckiej
                ten box zniknie.
              </p>

              <div className="space-y-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog("paa")}
                  disabled={resetting}
                  className="w-full justify-start border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-[11px]"
                >
                  <RotateCcw className="h-3 w-3 mr-1.5 shrink-0" />
                  Reset PAA (output kroku 1)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog("deterministic")}
                  disabled={resetting || !layers.L2_deterministic}
                  className="w-full justify-start border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-[11px]"
                >
                  <RotateCcw className="h-3 w-3 mr-1.5 shrink-0" />
                  Reset deterministic (output 2)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog("llm")}
                  disabled={resetting || !layers.L2_llm}
                  className="w-full justify-start border-violet-500/40 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 text-[11px]"
                >
                  <RotateCcw className="h-3 w-3 mr-1.5 shrink-0" />
                  Reset LLM Sonnet (output 3)
                </Button>
                <div className="pt-2 mt-2 border-t border-amber-500/20">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFetchPaa(true)}
                    disabled={fetchPaa.isPending}
                    className="w-full justify-start text-[10px] text-muted-foreground"
                    title="Force: pobiera PAA od zera, ignoruje cache 30d (~$1)"
                  >
                    ⚠ Force PAA (pobierz wszystko, ~$1)
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {(fullAudit.isPending || fetchPaa.isPending) && (
            <div className="mt-3 space-y-0.5 max-h-32 overflow-y-auto font-mono text-[11px] text-muted-foreground border rounded p-2 bg-muted/30">
              {logs.slice(-20).map((log, i) => (
                <div key={i}>{log}</div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!report && !fullAudit.isPending && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Brak audytu. Kliknij <strong>"Przelicz audyt"</strong> aby wygenerować pierwszy raport.
          </CardContent>
        </Card>
      )}

      {report && (
        <>
          {/* 5 kafelków Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <LayerTile
              label="L1 Post-validate"
              score={layers.L1 ? 0 : undefined}
              max={undefined}
              verdict="pass"
              icon={<Check className="h-3 w-3" />}
              detail={layers.L1 ? `${(layers.L1 as { orphansFixed: number }).orphansFixed || 0} fixed` : "—"}
            />
            <LayerTile
              label="L2 Structural"
              score={(layers.L2_deterministic as { score?: number })?.score}
              max={(layers.L2_deterministic as { max?: number })?.max}
              verdict={(layers.L2_deterministic as { verdict?: AuditVerdict })?.verdict}
              icon={<Activity className="h-3 w-3" />}
              detail={
                layers.L2_deterministic
                  ? `${(layers.L2_deterministic as { duplicate_groups?: unknown[] }).duplicate_groups?.length ?? 0} dupl · ${(layers.L2_deterministic as { cross_seed_bridges?: number }).cross_seed_bridges ?? 0} bridges`
                  : undefined
              }
            />
            <LayerTile
              label="L2 LLM (SRO)"
              score={(layers.L2_llm as { score?: number })?.score}
              max={(layers.L2_llm as { max?: number })?.max}
              verdict={(layers.L2_llm as { verdict?: AuditVerdict })?.verdict}
              icon={<Sparkles className="h-3 w-3" />}
              detail={
                layers.L2_llm
                  ? `${((layers.L2_llm as { per_seed?: unknown[] }).per_seed ?? []).length} seedów · $${((layers.L2_llm as { cost_usd?: number }).cost_usd ?? 0).toFixed(3)}`
                  : "nie odpalony"
              }
            />
            <LayerTile
              label="L3 Semantic"
              score={(layers.L3 as { score?: number })?.score}
              max={(layers.L3 as { max?: number })?.max}
              verdict={(layers.L3 as { verdict?: AuditVerdict })?.verdict}
              icon={<Target className="h-3 w-3" />}
              detail={
                layers.L3
                  ? `${(layers.L3 as { stranded_spokes?: { count?: number } }).stranded_spokes?.count ?? 0} stranded`
                  : undefined
              }
            />
            <LayerTile
              label="L5 Coverage"
              score={(layers.L5_coverage as { score?: number })?.score}
              max={(layers.L5_coverage as { max?: number })?.max}
              verdict={(layers.L5_coverage as { verdict?: AuditVerdict })?.verdict}
              icon={<Zap className="h-3 w-3" />}
              detail={
                layers.L5_coverage
                  ? `${Math.round(((layers.L5_coverage as { matched_ratio_global?: number }).matched_ratio_global ?? 0) * 100)}% matched`
                  : undefined
              }
            />
          </div>

          {/* Per-layer collapsible details — domyślnie L2-det otwarte (najszybciej wiadomo co działa) */}
          <Accordion type="multiple" defaultValue={["L2-det"]} className="space-y-2">
            {/* L2 deterministic */}
            {layers.L2_deterministic && (
              <AccordionItem value="L2-det" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 text-base">
                  <div className="flex items-center gap-3 w-full">
                    <Activity className="h-5 w-5" />
                    <span className="font-semibold">L2 Structural — deterministic</span>
                    <div className="ml-auto pr-2">
                      <ScoreBar
                        score={(layers.L2_deterministic as { score: number }).score}
                        maxScore={(layers.L2_deterministic as { max: number }).max}
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <L2DeterministicDetail data={layers.L2_deterministic as unknown as Record<string, unknown>} />
                </AccordionContent>
              </AccordionItem>
            )}

            {/* L2 LLM */}
            {layers.L2_llm && (
              <AccordionItem value="L2-llm" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 text-base">
                  <div className="flex items-center gap-3 w-full">
                    <Sparkles className="h-5 w-5 text-violet-600" />
                    <span className="font-semibold">L2 LLM — SRO 5 warstw (Sonnet 4.6)</span>
                    <div className="ml-auto pr-2">
                      <ScoreBar
                        score={(layers.L2_llm as { score: number }).score}
                        maxScore={(layers.L2_llm as { max: number }).max}
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <L2LlmDetail data={layers.L2_llm as unknown as Record<string, unknown>} />
                </AccordionContent>
              </AccordionItem>
            )}

            {/* L3 */}
            {layers.L3 && (
              <AccordionItem value="L3" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 text-base">
                  <div className="flex items-center gap-3 w-full">
                    <Target className="h-5 w-5" />
                    <span className="font-semibold">L3 Semantic — stranded spokes</span>
                    <div className="ml-auto pr-2">
                      <ScoreBar
                        score={(layers.L3 as { score: number }).score}
                        maxScore={(layers.L3 as { max: number }).max}
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <L3Detail data={layers.L3 as unknown as Record<string, unknown>} />
                </AccordionContent>
              </AccordionItem>
            )}

            {/* L5 */}
            {layers.L5_coverage && (
              <AccordionItem value="L5" className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 text-base">
                  <div className="flex items-center gap-3 w-full">
                    <Zap className="h-5 w-5" />
                    <span className="font-semibold">L5 Coverage — wp_posts cross-ref</span>
                    <div className="ml-auto pr-2">
                      <ScoreBar
                        score={(layers.L5_coverage as { score: number }).score}
                        maxScore={(layers.L5_coverage as { max: number }).max}
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <L5Detail data={layers.L5_coverage as unknown as Record<string, unknown>} />
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Violations accordion */}
          {allIssues.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Violations ({allIssues.length})
                </CardTitle>
                <CardDescription>
                  <span className="text-red-600 mr-3">ERROR: {errorCount}</span>
                  <span className="text-amber-600 mr-3">WARNING: {warningCount}</span>
                  <span className="text-sky-600">INFO: {infoCount}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {allIssues.slice(0, 50).map((issue, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border p-2 bg-background/60 text-xs space-y-1"
                  >
                    <div className="flex items-start gap-2">
                      {severityIcon(issue.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge variant="outline" className="text-[9px]">
                            {issue.layer}
                          </Badge>
                          <Badge variant="outline" className="text-[9px]">
                            {issue.dimension}
                          </Badge>
                        </div>
                        <p className="text-foreground">{issue.message}</p>
                        {issue.affected_slugs && issue.affected_slugs.length > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            <strong>Affected:</strong> {issue.affected_slugs.slice(0, 10).join(", ")}
                          </p>
                        )}
                        {issue.suggested_fix && (
                          <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                            💡 {issue.suggested_fix}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Legacy audit widoczny w JSONB, nie w UI */}
          {report.legacy_scn_audit && (
            <p className="text-[10px] text-muted-foreground text-center">
              ℹ️ Stary audyt v1 zachowany w <code>audit_report.legacy_scn_audit</code> (backward-compat, tylko w DB).
            </p>
          )}
        </>
      )}

      {/* PAA viewer — lista nodow z pobranymi pytaniami Google PAA */}
      <Dialog open={showPaaDialog} onOpenChange={setShowPaaDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-violet-600" />
              Pobrane PAA (People Also Ask) — {paaNodesQuery.data?.length ?? 0} nodow
            </DialogTitle>
            <DialogDescription>
              Dane z DataForSEO (Google PAA), cache 30 dni. Zapisane w{" "}
              <code className="text-[10px]">thematic_nodes.paa_preview</code>.
              Uzywane przez L3 audit (stranded spoke detection) + Content Engine fan-out.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {paaNodesQuery.isLoading && (
              <p className="text-sm text-muted-foreground">Ladowanie...</p>
            )}
            {!paaNodesQuery.isLoading && (paaNodesQuery.data?.length ?? 0) === 0 && (
              <div className="p-4 border border-dashed rounded-md text-center text-sm text-muted-foreground">
                Zaden node nie ma jeszcze PAA. Kliknij <strong>1. Pobierz PAA</strong> zeby pobrac.
              </div>
            )}
            {paaNodesQuery.data?.map((node) => {
              const questions = node.paa_preview ?? [];
              return (
                <div
                  key={node.id}
                  className="rounded-md border border-border bg-muted/20 p-3 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{node.title || node.slug}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        <code className="text-[10px]">{node.slug}</code>
                        {node.canonical_question && (
                          <>
                            {" — "}
                            <span className="italic">"{node.canonical_question}"</span>
                          </>
                        )}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {questions.length} {questions.length === 1 ? "pytanie" : "pytan"}
                    </Badge>
                  </div>
                  {questions.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">
                      Google nie zwrocil PAA dla tego node'a (fetched =&gt; empty).
                    </p>
                  ) : (
                    <ol className="list-decimal list-inside space-y-1 text-xs text-foreground/90">
                      {questions.map((q, idx) => (
                        <li key={idx}>{q.question}</li>
                      ))}
                    </ol>
                  )}
                  {node.paa_fetched_at && (
                    <p className="text-[10px] text-muted-foreground">
                      Pobrane:{" "}
                      {formatDistanceToNow(new Date(node.paa_fetched_at), {
                        addSuffix: true,
                        locale: pl,
                      })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset dialog — selektywny per warstwa/PAA */}
      <AlertDialog open={!!showResetDialog} onOpenChange={(o) => !o && setShowResetDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {showResetDialog === "paa"
                ? "Wyzerować PAA (output kroku 1)?"
                : showResetDialog === "deterministic"
                ? "Wyzerować deterministic (output kroku 2)?"
                : "Wyzerować LLM Sonnet (output kroku 3)?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {showResetDialog === "paa" ? (
                <>
                  Wyczyści <code>paa_preview = []</code> i <code>paa_fetched_at = NULL</code> dla wszystkich
                  nodów mapy.
                  <br />
                  <strong>Zachowane:</strong> audit_report (L1-L5 + LLM), cała struktura mapy.
                  <br /><br />
                  Klik "1 Pobierz PAA" odpali świeży fetch z DataForSEO.
                </>
              ) : showResetDialog === "deterministic" ? (
                <>
                  Usunie z <code>audit_report.layers</code>: L1, L2_deterministic, L3, L5_coverage.
                  <br />
                  <strong>Zachowane:</strong> L2_llm (jeśli był), PAA baseline, cache 30d.
                  <br /><br />
                  Klik "2 Przelicz audyt" odbuduje te warstwy.
                </>
              ) : (
                <>
                  Usunie z <code>audit_report.layers</code> klucz: L2_llm.
                  <br />
                  <strong>Zachowane:</strong> L1, L2_deterministic, L3, L5_coverage, PAA baseline.
                  <br /><br />
                  Klik "3 Pełny audyt + LLM Sonnet" odpali nowy LLM run (koszt zależny od liczby seedów).
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (showResetDialog === "paa") handleResetPaa();
                else if (showResetDialog) handleResetAudit(showResetDialog);
              }}
              disabled={resetting}
              className={showResetDialog === "llm" ? "bg-violet-600 hover:bg-violet-700" : "bg-red-600 hover:bg-red-700"}
            >
              {resetting ? "Resetuję..." : "Wyzeruj"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Map Repair Engine v1 modal */}
      <MapRepairModal mapId={mapId} open={showRepairModal} onOpenChange={setShowRepairModal} />

      {/* STOP-4 link-resolver — Re-heal pending links confirm */}
      <AlertDialog
        open={showHealDialog}
        onOpenChange={(o) => !o && !healing && setShowHealDialog(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-emerald-500" />
              Re-heal pending links (cała mapa)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Skanuje wpisy w TEJ mapie z markerem{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[11px]">
                    data-pending-link
                  </code>{" "}
                  i podmienia na{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[11px]">
                    &lt;a href&gt;
                  </code>{" "}
                  dla targetów które są już live.
                </p>
                <p className="text-muted-foreground">
                  Per-wpis: UPDATE w DB final_html + WP REST{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                    POST /posts/{"{id}"}
                  </code>{" "}
                  (tylko pole content, reszta nietknięta).
                </p>
                <p className="text-xs text-muted-foreground italic">
                  Scoped do tej mapy. Idempotent — re-run bez efektu na
                  już-zhealowane linki. Wpisy bez markerów: nietknięte.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={healing}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleReHealAllPending();
              }}
              disabled={healing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {healing ? "Heal w toku..." : "Tak, uruchom heal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Per-layer detail components (read-only)
// ─────────────────────────────────────────────────────────────

function L2DeterministicDetail({ data }: { data: Record<string, unknown> }) {
  const orphans = data.orphans as { count: number; ratio: number; affected: string[] } | undefined;
  const noIn = data.no_incoming_spokes as { count: number; ratio: number; affected: string[] } | undefined;
  const dupes = data.duplicate_groups as Array<{
    entity: string;
    intent: string;
    count: number;
    jaccard: number;
    severity: string;
    affected_slugs: string[];
  }> | undefined;
  const imb = data.seed_imbalance as { cv: number; min_count: number; max_count: number } | undefined;
  const seedsWO = data.seeds_without_nodes as string[] | undefined;
  const crossSeed = data.cross_seed_bridges as number | undefined;
  const intraSeed = data.intra_seed_relations as number | undefined;
  const degDist = data.degree_distribution as Array<{ incoming: number; count: number }> | undefined;

  return (
    <div className="space-y-4 text-sm">
      {/* Opis warstwy */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-sm">
          <strong>Co to znaczy?</strong> Warstwa deterministyczna liczy metryki topologii grafu mapy:
          orphany (nody bez parenta), duplikaty (nody o tej samej encji+intencji), balans seedów,
          cross-seed bridges. Zero kosztu, ~2s.
        </p>
      </div>

      {/* Kluczowe metryki */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Orphans" value={orphans?.count ?? 0} sub={`${orphans?.ratio ?? 0}% mapy`} critical={(orphans?.count ?? 0) > 0} />
        <Metric label="No incoming" value={noIn?.count ?? 0} sub={`${noIn?.ratio ?? 0}% mapy`} critical={(noIn?.count ?? 0) > 0} />
        <Metric label="Cross-seed bridges" value={crossSeed ?? 0} sub={`intra-seed: ${intraSeed ?? 0}`} />
        <Metric label="Seed imbalance CV" value={imb?.cv ?? 0} sub={`min ${imb?.min_count ?? 0} / max ${imb?.max_count ?? 0}`} />
      </div>

      {dupes && dupes.length > 0 && (
        <SectionBox
          title="Duplicate groups"
          description="Grupy nodów o tej samej encji i intencji (np. 3× 'california roll' z intencją informational). Wskazują scope overlap — trzeba zmergować lub zmienić entity/intent jednego z nich."
          badge={<Badge variant="outline" className="text-xs">{dupes.length} grup</Badge>}
          maxHeight="320px"
        >
          <div className="space-y-2">
            {dupes.map((d, i) => {
              const sevColor = d.severity === "error"
                ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30"
                : d.severity === "warning"
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30"
                : "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30";
              return (
                <div key={i} className="rounded-lg border bg-muted/30 p-3">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge className={`text-xs px-2 py-0.5 border ${sevColor}`}>
                      {d.severity}
                    </Badge>
                    <span className="font-mono text-sm font-semibold">{d.entity} × {d.intent}</span>
                    <span className="text-xs text-muted-foreground">
                      {d.count}× nodów · Jaccard {d.jaccard}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {d.affected_slugs.slice(0, 10).map((slug, si) => (
                      <Badge key={si} variant="outline" className="text-xs">{slug}</Badge>
                    ))}
                    {d.affected_slugs.length > 10 && (
                      <span className="text-xs text-muted-foreground">+{d.affected_slugs.length - 10}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionBox>
      )}

      {orphans && orphans.affected && orphans.affected.length > 0 && (
        <SectionBox
          title="Orphan slugs"
          description="Nody bez parent w hierarchii (żadna relacja przychodząca poza 'sibling'). Zwykle znak błędu w linkingu — powinny być pod pillarem lub seedem."
          badge={<Badge variant="outline" className="text-xs">{orphans.affected.length}</Badge>}
          maxHeight="200px"
        >
          <div className="flex flex-wrap gap-1">
            {orphans.affected.map((s, i) => (
              <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
            ))}
          </div>
        </SectionBox>
      )}

      {seedsWO && seedsWO.length > 0 && (
        <SectionBox
          title="Seeds without nodes"
          description="Seedy (klastry) w których LLM nie wygenerował żadnego nodu. Pusty klaster = niekompletna mapa."
          badge={<Badge variant="destructive" className="text-xs">{seedsWO.length}</Badge>}
          maxHeight="180px"
        >
          <div className="flex flex-wrap gap-1.5">
            {seedsWO.map((s, i) => (
              <Badge key={i} variant="destructive" className="text-xs px-2 py-0.5">{s}</Badge>
            ))}
          </div>
        </SectionBox>
      )}

      {degDist && degDist.length > 0 && (
        <SectionBox
          title="Incoming degree distribution"
          description="Histogram: oś X = ile przychodzących relacji ma node, oś Y = ile takich nodów w mapie. Duży słupek przy 0 = dużo orphanów."
          maxHeight="200px"
        >
          <div className="flex items-end gap-1.5 h-32 bg-muted/30 rounded p-2">
            {degDist.map((d, i) => {
              const maxCount = Math.max(...degDist.map((x) => x.count));
              const h = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
              return (
                <div key={i} className="flex flex-col items-center gap-0.5 flex-1 min-w-0">
                  <div className="text-xs tabular-nums font-semibold">{d.count}</div>
                  <div className="w-full bg-violet-500 rounded-t min-h-[2px]" style={{ height: `${h}%` }} title={`${d.incoming} incoming: ${d.count} nodes`} />
                  <div className="text-xs tabular-nums text-muted-foreground">{d.incoming}</div>
                </div>
              );
            })}
          </div>
        </SectionBox>
      )}
    </div>
  );
}

type SroLayerData = {
  score: number;
  max: number;
  verdict: AuditVerdict;
  issues?: AuditIssue[];
};

type L2LlmPerSeed = {
  seed_id?: string;
  seed_name: string;
  seed_verdict: AuditVerdict;
  seed_total_score: number;
  sro_layers?: Record<string, SroLayerData>;
  strategic_recommendations?: string[];
};

const SRO_LAYER_LABELS: Record<string, string> = {
  macrosemantics: "Makro (architektura klastra)",
  microsemantics: "Mikro (passage engineering)",
  technical_eligibility: "Techniczna (crawl/schema)",
  trust_calibration: "Zaufanie (E-E-A-T)",
  query_semantics: "Query (fan-out/intent)",
};

const SRO_LAYER_KEYS = [
  "macrosemantics",
  "microsemantics",
  "technical_eligibility",
  "trust_calibration",
  "query_semantics",
] as const;

function L2LlmDetail({ data }: { data: Record<string, unknown> }) {
  const perSeed = (data.per_seed as L2LlmPerSeed[] | undefined) ?? [];
  const aggregate = data.sro_layers_aggregate as Record<string, SroLayerData> | undefined;
  const critical = (data.critical_findings as AuditIssue[] | undefined) ?? [];
  const recs = (data.strategic_recommendations as string[] | undefined) ?? [];
  const costUsd = (data.cost_usd as number | undefined) ?? 0;
  const tokensIn = (data.tokens_in as number | undefined) ?? 0;
  const tokensOut = (data.tokens_out as number | undefined) ?? 0;
  const durationMs = (data.duration_ms as number | undefined) ?? 0;
  const skipped = data.skipped as boolean | undefined;
  const skipReason = data.skip_reason as string | undefined;

  const [expandedSeed, setExpandedSeed] = useState<string | null>(null);
  const [layerFilter, setLayerFilter] = useState<string | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<"all" | "error" | "warning" | "info">("all");
  const [allExpanded, setAllExpanded] = useState(false);

  if (skipped) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm">
        <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">⚠ LLM audyt pominięty</p>
        <p className="text-muted-foreground">{skipReason || "Brak powodu"}</p>
      </div>
    );
  }

  const sortedSeeds = [...perSeed].sort((a, b) => a.seed_total_score - b.seed_total_score);

  return (
    <div className="space-y-4 text-sm">
      {/* Opis warstwy */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-sm">
          <strong>Co to znaczy?</strong> Claude Sonnet 4.6 ocenia każdy seed według
          5 warstw SRO (Koray Tuğberk Gübür framework):
          <strong> Makro</strong> (architektura klastra, closed-loop topology),
          <strong> Mikro</strong> (passage engineering, BLUF, entity anchoring),
          <strong> Technical</strong> (crawl/schema/SSR),
          <strong> Trust</strong> (E-E-A-T, authority signals),
          <strong> Query</strong> (fan-out, intent ladder). Każda warstwa 0-20 pkt, agregat = średnia.
        </p>
      </div>

      {/* Meta bar — czytelne kafelki */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Metric label="⏱ Czas" value={`${(durationMs / 1000).toFixed(1)}s`} />
        <Metric label="🧠 Seedy" value={perSeed.length} />
        <Metric label="💰 Koszt" value={`$${costUsd.toFixed(4)}`} />
        <Metric label="📥→📤 Tokeny" value={`${tokensIn.toLocaleString()}→${tokensOut.toLocaleString()}`} />
        <Metric label="🔥 Critical" value={critical.length} critical={critical.length > 0} />
      </div>

      {/* Aggregate per-layer — większe kafelki */}
      {aggregate && (
        <div>
          <h4 className="font-semibold text-sm mb-2">Agregat 5 warstw SRO (średnia z seedów)</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {SRO_LAYER_KEYS.map((k) => {
              const issueCount = aggregate[k]?.issues?.length ?? 0;
              return (
                <div key={k} className={`rounded-lg border p-3 ${verdictColor(aggregate[k]?.verdict)}`}>
                  <div className="text-xs uppercase font-semibold leading-tight mb-1.5">{SRO_LAYER_LABELS[k]}</div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-bold tabular-nums">
                      {aggregate[k]?.score ?? 0}
                    </span>
                    <span className="text-sm opacity-70">/{aggregate[k]?.max ?? 20}</span>
                  </div>
                  {issueCount > 0 && (
                    <div className="text-xs mt-1 font-medium">{issueCount} issues</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-seed ranking — scrollowany box */}
      {sortedSeeds.length > 0 && (
        <SectionBox
          title="Per-seed ranking"
          description={`Każdy z ${sortedSeeds.length} seedów oceniony osobno przez Sonnet 4.6. Sortowane od najgorszego. Kliknij seed żeby zobaczyć szczegóły per-warstwa + issues + fix.`}
          badge={
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAllExpanded((v) => !v)}
              className="text-xs h-6 px-2 ml-auto"
            >
              {allExpanded ? "Zwiń wszystkie" : "Rozwiń wszystkie"}
            </Button>
          }
          maxHeight="500px"
        >
          <div className="space-y-2">
            {sortedSeeds.map((s, i) => {
              const key = s.seed_id || s.seed_name + i;
              const isExpanded = allExpanded || expandedSeed === key;
              const issuesCount = SRO_LAYER_KEYS.reduce(
                (sum, k) => sum + (s.sro_layers?.[k]?.issues?.length ?? 0),
                0,
              );
              const errCount = SRO_LAYER_KEYS.reduce(
                (sum, k) =>
                  sum + (s.sro_layers?.[k]?.issues?.filter((iss) => iss.severity === "error").length ?? 0),
                0,
              );
              return (
                <div key={key} className="rounded-lg border bg-muted/20">
                  <button
                    type="button"
                    onClick={() => !allExpanded && setExpandedSeed(isExpanded ? null : key)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-muted/40 text-left"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <span className="flex-1 min-w-0 font-semibold text-sm break-words">{s.seed_name}</span>
                    <span className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      {errCount > 0 && (
                        <Badge className="bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 text-xs px-2">
                          ERR {errCount}
                        </Badge>
                      )}
                      {issuesCount > 0 && (
                        <span className="text-xs text-muted-foreground">{issuesCount} issues</span>
                      )}
                      <span className="text-sm tabular-nums font-mono font-semibold">
                        {s.seed_total_score}/100
                      </span>
                      <Badge className={`${verdictColor(s.seed_verdict)} border text-xs px-2`}>
                        {s.seed_verdict}
                      </Badge>
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="border-t px-3 pb-3 pt-3 space-y-3">
                      {/* Per-layer kafelki */}
                      <div className="grid grid-cols-5 gap-2">
                        {SRO_LAYER_KEYS.map((k) => {
                          const layer = s.sro_layers?.[k];
                          if (!layer) return (
                            <div key={k} className="opacity-30 rounded border p-2 text-center text-xs">
                              <div className="text-xs uppercase">{k.split("_")[0]}</div>
                              <div className="text-sm">—</div>
                            </div>
                          );
                          return (
                            <div key={k} className={`rounded border p-2 ${verdictColor(layer.verdict)}`}>
                              <div className="text-xs uppercase font-semibold truncate">{k.split("_")[0]}</div>
                              <div className="text-base font-bold tabular-nums">
                                {layer.score}/{layer.max}
                              </div>
                              {layer.issues && layer.issues.length > 0 && (
                                <div className="text-xs mt-0.5">{layer.issues.length} issues</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Issues per-warstwa */}
                      {SRO_LAYER_KEYS.map((k) => {
                        const issues = s.sro_layers?.[k]?.issues ?? [];
                        if (issues.length === 0) return null;
                        return (
                          <div key={k}>
                            <p className="text-xs uppercase font-semibold text-muted-foreground mb-1">
                              {SRO_LAYER_LABELS[k]} — {issues.length} issue{issues.length > 1 ? "s" : ""}
                            </p>
                            <div className="space-y-1.5">
                              {issues.map((iss, ii) => (
                                <div key={ii} className="flex gap-2 rounded-lg bg-background/80 border p-2.5">
                                  {severityIcon(iss.severity)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm break-words">{iss.message}</p>
                                    {iss.affected_slugs && iss.affected_slugs.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        <span className="text-xs text-muted-foreground">Affected:</span>
                                        {iss.affected_slugs.slice(0, 8).map((slug, si) => (
                                          <Badge key={si} variant="outline" className="text-xs">{slug}</Badge>
                                        ))}
                                        {iss.affected_slugs.length > 8 && (
                                          <span className="text-xs text-muted-foreground">+{iss.affected_slugs.length - 8}</span>
                                        )}
                                      </div>
                                    )}
                                    {iss.suggested_fix && (
                                      <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1.5 break-words">
                                        💡 <strong>Fix:</strong> {iss.suggested_fix}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {s.strategic_recommendations && s.strategic_recommendations.length > 0 && (
                        <div>
                          <p className="text-xs uppercase font-semibold text-muted-foreground mb-1">
                            Rekomendacje dla tego seeda
                          </p>
                          <ul className="list-disc ml-5 space-y-1 text-sm">
                            {s.strategic_recommendations.map((r, ri) => (
                              <li key={ri} className="break-words">{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SectionBox>
      )}

      {/* Critical findings z filtrami — scrollowany box */}
      {critical.length > 0 && (
        <SectionBox
          title="🔥 Critical findings"
          description="Wszystkie ERROR-owe findings z wszystkich seedów, zebrane w jedno miejsce. Filtr warstwa + severity pozwala zawęzić do konkretnego typu problemu."
          badge={<Badge className="bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30 text-xs">{critical.length}</Badge>}
          maxHeight="450px"
        >
          {/* Filtry */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3 text-xs pb-2 border-b">
            <span className="text-muted-foreground font-medium mr-1">Warstwa:</span>
            <button
              type="button"
              onClick={() => setLayerFilter("all")}
              className={`px-2.5 py-1 rounded-md border text-xs ${layerFilter === "all" ? "bg-violet-500/20 border-violet-500/50 font-semibold" : "border-border/60 hover:bg-muted"}`}
            >
              wszystko
            </button>
            {SRO_LAYER_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setLayerFilter(k)}
                className={`px-2.5 py-1 rounded-md border text-xs ${layerFilter === k ? "bg-violet-500/20 border-violet-500/50 font-semibold" : "border-border/60 hover:bg-muted"}`}
              >
                {k.split("_")[0]}
              </button>
            ))}
            <span className="mx-1 text-muted-foreground">|</span>
            <span className="text-muted-foreground font-medium mr-1">Severity:</span>
            {(["all", "error", "warning", "info"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSeverityFilter(s)}
                className={`px-2.5 py-1 rounded-md border text-xs ${severityFilter === s ? "bg-violet-500/20 border-violet-500/50 font-semibold" : "border-border/60 hover:bg-muted"}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {critical
              .filter((iss) => {
                if (layerFilter !== "all" && !iss.dimension.startsWith(layerFilter)) return false;
                if (severityFilter !== "all" && iss.severity !== severityFilter) return false;
                return true;
              })
              .map((iss, i) => (
                <div key={i} className="flex gap-2 rounded-lg bg-background/80 border p-2.5">
                  {severityIcon(iss.severity)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">{iss.dimension}</Badge>
                    </div>
                    <p className="text-sm break-words">{iss.message}</p>
                    {iss.affected_slugs && iss.affected_slugs.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <span className="text-xs text-muted-foreground">Affected:</span>
                        {iss.affected_slugs.slice(0, 10).map((slug, si) => (
                          <Badge key={si} variant="outline" className="text-xs">{slug}</Badge>
                        ))}
                      </div>
                    )}
                    {iss.suggested_fix && (
                      <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1.5 break-words">
                        💡 <strong>Fix:</strong> {iss.suggested_fix}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </SectionBox>
      )}

      {recs.length > 0 && (
        <SectionBox
          title="Globalne rekomendacje strategiczne"
          description="Top rekomendacje ze wszystkich seedów zagregowane w jedno miejsce — 'co zmienić najpierw' żeby poprawić SRO compliance całej mapy."
          maxHeight="260px"
        >
          <ul className="list-disc ml-5 space-y-1 text-sm">
            {recs.slice(0, 15).map((r, i) => (
              <li key={i} className="break-words">{r}</li>
            ))}
          </ul>
        </SectionBox>
      )}
    </div>
  );
}

function L3Detail({ data }: { data: Record<string, unknown> }) {
  const stranded = data.stranded_spokes as {
    count: number;
    ratio: number;
    severity: string;
    affected: Array<{ slug: string; document_role: string; search_volume: number; seed_name: string }>;
  } | undefined;
  const lowVal = data.low_value_definitions as {
    count: number;
    affected: Array<{ slug: string; search_volume: number; seed_name: string }>;
  } | undefined;
  const topoOrph = data.topology_orphans_with_no_demand as {
    count: number;
    affected: Array<{ slug: string; document_role: string; seed_name: string }>;
  } | undefined;
  const demand = data.demand_coverage_summary as {
    nodes_with_sv: number;
    nodes_with_paa: number;
    nodes_with_both: number;
    nodes_with_neither: number;
  } | undefined;

  return (
    <div className="space-y-4 text-sm">
      {/* Opis warstwy */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-sm">
          <strong>Co to znaczy?</strong> Warstwa semantyczna szuka nodów-sierot — takich które
          nie mają żadnego sygnału demand (brak SV w DataForSEO, brak PAA w Google),
          żadnego matchu z istniejącym wpisem i żadnej relacji przychodzącej. To kandydaci
          do usunięcia lub mergu (marnują crawl budget).
        </p>
      </div>

      {demand && (
        <div>
          <h4 className="font-semibold text-sm mb-2">Pokrycie demand (ile nodów ma sygnał popytu)</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Metric label="z SV (DataForSEO)" value={demand.nodes_with_sv} />
            <Metric label="z PAA (Google)" value={demand.nodes_with_paa} />
            <Metric label="oba sygnały" value={demand.nodes_with_both} />
            <Metric label="żadne (uwaga!)" value={demand.nodes_with_neither} critical={demand.nodes_with_neither > 0} />
          </div>
        </div>
      )}

      {stranded && stranded.count > 0 && (
        <SectionBox
          title="Stranded spokes"
          description="Nody evaluative (comparison/troubleshooting/guide) bez SV/PAA/matchu/incoming. Wysoka intencja komercyjna bez sygnału demand = zmarnowany wpis. Rozważ usunięcie lub zmianę scope."
          badge={<Badge variant="outline" className="text-xs">{stranded.count} · {stranded.ratio}% · {stranded.severity}</Badge>}
          maxHeight="280px"
        >
          <div className="space-y-1">
            {stranded.affected.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded shrink-0">{a.slug}</span>
                <Badge variant="outline" className="text-xs shrink-0">{a.document_role}</Badge>
                <span className="text-xs text-muted-foreground shrink-0">SV={a.search_volume}</span>
                <span className="text-xs text-muted-foreground truncate">seed: {a.seed_name}</span>
              </div>
            ))}
          </div>
        </SectionBox>
      )}

      {lowVal && lowVal.count > 0 && (
        <SectionBox
          title="Low-value definitions"
          description="Nody typu 'definicja' z SV<50 i bez PAA/matchu. Definicje są OK jako entity anchory, ale te nie mają żadnego demand — kandydat do mergu z nadrzędnym glossary."
          badge={<Badge variant="outline" className="text-xs">{lowVal.count}</Badge>}
          maxHeight="220px"
        >
          <div className="space-y-1">
            {lowVal.affected.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded shrink-0">{a.slug}</span>
                <span className="text-xs text-muted-foreground shrink-0">SV={a.search_volume}</span>
                <span className="text-xs text-muted-foreground truncate">seed: {a.seed_name}</span>
              </div>
            ))}
          </div>
        </SectionBox>
      )}

      {topoOrph && topoOrph.count > 0 && (
        <SectionBox
          title="Topology orphans z zerowym demand"
          description="Nody bez relacji przychodzącej i bez demand. Nie są evaluative (inna reguła) ale są ślepe — nikt do nich nie dolinkuje, Google ich nie szuka."
          badge={<Badge variant="outline" className="text-xs">{topoOrph.count}</Badge>}
          maxHeight="220px"
        >
          <div className="space-y-1">
            {topoOrph.affected.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded shrink-0">{a.slug}</span>
                <Badge variant="outline" className="text-xs shrink-0">{a.document_role}</Badge>
                <span className="text-xs text-muted-foreground truncate">seed: {a.seed_name}</span>
              </div>
            ))}
          </div>
        </SectionBox>
      )}

      {(!stranded || stranded.count === 0) && (!lowVal || lowVal.count === 0) && (!topoOrph || topoOrph.count === 0) && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
          <p className="font-semibold text-sm text-emerald-700 dark:text-emerald-400">
            ✓ Zero stranded spokes — mapa semantycznie czysta
          </p>
        </div>
      )}
    </div>
  );
}

function L5Detail({ data }: { data: Record<string, unknown> }) {
  const globalRatio = (data.matched_ratio_global as number) ?? 0;
  const perSeed = data.matched_ratio_per_seed as Array<{
    seed_name: string;
    total_nodes: number;
    matched: number;
    ratio: number;
    priority: number;
  }> | undefined;
  const ghosts = data.ghost_seeds as Array<{ seed_name: string; priority: number; severity: string }> | undefined;
  const rev = data.reverse_node_suggestions as Array<{ wp_title: string; extracted_entity: string; candidates: Array<{ seed_name: string; jaccard: number }> }> | undefined;
  const orphans = data.orphan_existing_posts as Array<{ wp_title: string; extracted_entity: string | null; wp_url: string | null }> | undefined;

  const globalPct = Math.round(globalRatio * 100);
  const globalColor = globalPct >= 50 ? "text-emerald-600 dark:text-emerald-400" : globalPct >= 20 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";

  return (
    <div className="space-y-4 text-sm">
      {/* Opis warstwy */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
        <p className="text-sm">
          <strong>Co to znaczy?</strong> Warstwa coverage krzyżuje mapę tematyczną z istniejącymi
          wpisami WP (<code>wp_posts</code>). Szuka: seedów bez żadnego matchu (ghost),
          wpisów "in-scope" bez odpowiednika w mapie (LUKA #1) oraz statystyk pokrycia.
          Odpala się automatycznie po matcherze / sync SEO / decision engine.
        </p>
      </div>

      {/* Global ratio */}
      <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
        <div className="shrink-0">
          <span className="text-xs uppercase text-muted-foreground block">Global matched ratio</span>
          <span className={`text-3xl font-bold tabular-nums ${globalColor}`}>{globalPct}%</span>
          <span className="text-xs text-muted-foreground block">nodów zmatchowanych do wpisów WP</span>
        </div>
        <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${globalPct >= 50 ? "bg-emerald-500" : globalPct >= 20 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${globalPct}%` }} />
        </div>
      </div>

      {perSeed && perSeed.length > 0 && (
        <SectionBox
          title="Matched per seed"
          description="Ile nodów każdego seeda ma odpowiednik wśród istniejących wpisów WP. Niskie liczby = dużo nowego contentu do napisania, wysokie = dużo do refurbish/keep."
          maxHeight="300px"
        >
          <div className="space-y-1.5">
            {perSeed.map((s, i) => {
              const pct = Math.round(s.ratio * 100);
              const color = pct >= 50 ? "bg-emerald-500" : pct >= 20 ? "bg-amber-500" : "bg-red-500";
              const textColor = pct >= 50 ? "text-emerald-600 dark:text-emerald-400" : pct >= 20 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
              return (
                <div key={i} className="flex items-center gap-3 py-1">
                  <span className="text-sm flex-1 min-w-0 break-words">{s.seed_name}</span>
                  <div className="w-40 h-2.5 bg-muted rounded-full overflow-hidden shrink-0">
                    <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-sm font-semibold tabular-nums w-24 text-right ${textColor}`}>
                    {s.matched}/{s.total_nodes} · {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </SectionBox>
      )}

      {ghosts && ghosts.length > 0 && (
        <SectionBox
          title="Ghost seeds"
          description="Seedy z 0% matched — żaden node tego seeda nie pasuje do istniejącego wpisu. Priorytet seeda skalował severity (p1 = error, p2-3 = warning, p4+ = info)."
          badge={<Badge variant="outline" className="text-xs">{ghosts.length} seedów</Badge>}
          maxHeight="240px"
        >
          <div className="space-y-1.5">
            {ghosts.map((g, i) => (
              <div key={i} className="flex items-center gap-2 rounded border bg-background/60 p-2">
                <Badge variant="outline" className="text-xs px-2 py-0.5">priority p{g.priority}</Badge>
                <Badge className={`text-xs px-2 py-0.5 border ${g.severity === "error" ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" : g.severity === "warning" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" : "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30"}`}>
                  {g.severity}
                </Badge>
                <span className="text-sm font-medium">{g.seed_name}</span>
              </div>
            ))}
          </div>
        </SectionBox>
      )}

      {rev && rev.length > 0 && (
        <SectionBox
          title="Reverse node suggestions (LUKA #1)"
          description="Wpisy WP sklasyfikowane jako in-scope, które pasują tematycznie do któregoś seeda, ale nie mają odpowiednika w mapie. Sugestia: dodać node o tym entity pod wskazanym seedem."
          badge={<Badge variant="outline" className="text-xs">{rev.length} wpisów</Badge>}
          maxHeight="360px"
        >
          <div className="space-y-2">
            {rev.map((r, i) => (
              <div key={i} className="rounded-lg border bg-muted/30 p-3">
                <p className="font-medium text-sm mb-1.5">{r.wp_title}</p>
                <div className="text-xs flex flex-wrap items-center gap-1.5">
                  <span className="text-muted-foreground">entity:</span>
                  <strong className="text-foreground">{r.extracted_entity}</strong>
                  <span className="text-muted-foreground">→ kandydaci:</span>
                  {r.candidates.map((c, ci) => (
                    <Badge key={ci} variant="outline" className="text-xs">
                      {c.seed_name} <span className="opacity-70 ml-1">({c.jaccard.toFixed(2)})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SectionBox>
      )}

      {orphans && orphans.length > 0 && (
        <SectionBox
          title="Orphan in-scope wpisy WP"
          description="Wpisy WP sklasyfikowane jako in-scope ale bez matchu do noda ani decyzji w publish queue. Wymagają ręcznej decyzji: keep / refurbish / delete / consolidate."
          badge={<Badge variant="outline" className="text-xs">{orphans.length}</Badge>}
          maxHeight="280px"
        >
          <ul className="space-y-1 text-sm">
            {orphans.map((o, i) => (
              <li key={i} className="flex gap-2 py-0.5">
                <span className="text-muted-foreground shrink-0">•</span>
                <span className="flex-1 break-words">
                  {o.wp_title}
                  {o.extracted_entity && <span className="text-muted-foreground ml-1">({o.extracted_entity})</span>}
                </span>
              </li>
            ))}
          </ul>
        </SectionBox>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  critical,
}: {
  label: string;
  value: number | string;
  sub?: string;
  critical?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-3 ${critical ? "border-red-500/50 bg-red-500/10" : "bg-muted/30"}`}>
      <p className="text-xs uppercase text-muted-foreground tracking-wider">{label}</p>
      <p className={`text-2xl font-bold tabular-nums mt-0.5 ${critical ? "text-red-600 dark:text-red-400" : ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function StrandedList({
  title,
  severity,
  affected,
}: {
  title: string;
  severity: string;
  affected: string[];
}) {
  const color = severity === "error"
    ? "border-red-500/40 bg-red-500/5"
    : severity === "warning"
    ? "border-amber-500/40 bg-amber-500/5"
    : "border-sky-500/40 bg-sky-500/5";
  return (
    <div className={`rounded-lg border ${color} p-3`}>
      <p className="font-semibold text-sm mb-2">{title}</p>
      <ul className="space-y-1 text-sm">
        {affected.map((a, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-muted-foreground shrink-0">•</span>
            <span className="break-words">{a}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
